import * as ko from "knockout";
import * as _ from "lodash";
import * as Constants from "../common/constants";
import * as EventUtil from "../common/eventUtil";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as Util from "../common/util";
import * as UserData from "../data/userData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

export class UserController implements IController {
    // references to DOM elements
    private container: JQuery;
    private viewModel: any;
    private userID: number;
    private fieldsOnLoad: any = {};

    constructor() {
        const me: UserController = this;

        this.viewModel = {
            ID: ko.observable(0),
            CustomerID: ko.observable(0),
            UserName: ko.observable().extend({ required: true }),
            Email: ko.observable(),
            FirstName: ko.observable().extend({ required: true }),
            LastName: ko.observable().extend({ required: true }),
            IsActive: ko.observable(true),
            UserCustomerIsActive: ko.observable(true),
            TimeZoneID: ko.observable(0),
            IsTimeZoneIDMissing: ko.observable(false),
            Roles: ko.observableArray(),
            ShowPassword: ko.observable(false),

            // values for drop down lists
            TimeZones: ko.observableArray<Data.ICodeModel<number>>(),

            // password requirements
            MinimumPasswordLength: ko.observable(0),
            PasswordComplexityRequirements: ko.observable(""),
            ResetPasswordOnNextLogin: ko.observable(true),

            Errors: ko.observable(false),
            passwordVisible(user: any): void {
                user.ShowPassword(!user.ShowPassword());
            },
            inactivateUser(user: any): void {
                UI.showConfirmDialog("Are you sure you want to make user inactive?",
                    () => { me.toggleUserStatus(); });
            },
            activateUser(user: any): void {
                UI.showConfirmDialog("Are you sure you want to make user active?",
                    () => { me.toggleUserStatus(); });
            },
        };
        this.viewModel.Password = ko.observable("").extend({
            required: {
                message: "Please enter a password",
                params: true,
                onlyIf: me.viewModel.ShowPassword,
            },
        });
        this.viewModel.ConfirmPassword = ko.observable("").extend({
            required: {
                message: "Please confirm password",
                params: true,
                onlyIf: me.viewModel.ShowPassword,
            },
        });

        this.viewModel.errors = ko.validation.group(this.viewModel, { deep: true });
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        ko.applyBindings(this.viewModel, container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        // if user ID is passed passed as parameters, then we are
        // editing an existing user.  Otherwise we are adding
        // a new user.
        if (params != null && params.length === 1) {
            this.userID = params[0];
        } else {
            this.userID = 0;
        }

        this.loadPasswordRequirements();

        return this.loadRecord(this.userID);
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.viewModel);
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        let appRouterID: string = "User";
        if (params != null && params.length === 1) {
            appRouterID += params[0];
        }
        return appRouterID;
    }

    public save(): JQueryPromise<any> {
        const me: UserController = this;
        const app: IApplication = Global.app();
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (this.isValid()) {
            waitContainer = UI.showWaitIndicator(me.container);
            UserData.SaveUser(ko.toJS(this.viewModel))
                .done((response: Data.IResponse<UserData.IUserResponse>) => {
                    if (response.Success) {
                        if (me.viewModel.ID() <= 0) {
                            me.viewModel.ID(response.Payload.ID);
                            // add a new record, so update the URL
                            me.updateURL(response.Payload.ID, me.viewModel.UserName());
                        } else {
                            // updating an existing record
                            app.pageManager().refreshCurrentPageTitle();
                        }
                        me.loadRecord(response.Payload.ID);
                        UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                        deferred.resolve();
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        } else {
            // not all valid fields are entered
            deferred.reject();
        }

        return deferred.promise();
    }

    // returns a title for the page
    public title(): string {
        return "User";
    }

    public subTitle(): string {
        if (this.viewModel != null && this.userID != null) {
            return this.viewModel.UserName();
        } else {
            return "New";
        }
    }

    // used to update the URL when a new record is added
    private updateURL(newID: number, UserName: string): void {
        const app: IApplication = Global.app();
        const routeChange: EventUtil.IRouteChange = {
            appRouterID: this.appRouterID([]),
            url: "/User/" + newID,
            title: this.title(),
            subTitle: UserName,
            newAppRouterID: this.appRouterID([newID]),
        };

        app.events().trigger(EventUtil.EVENT_ROUTECHANGE, routeChange);
    }
    private loadRecord(userID: number): JQueryPromise<any> {
        const me: UserController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        const userRequest: UserData.IUserRequest = {
            UserID: userID,
        };

        UserData.GetUser(userRequest)
            .done((response: Data.IResponse<UserData.IUserEditMaster>) => {
                if (response.Success) {
                    me.viewModel.Roles([]);
                    me.viewModel.TimeZones([]);

                    me.viewModel.ID(response.Payload.ID);
                    me.viewModel.CustomerID(response.Payload.CustomerID);
                    if (!Util.isNullOrEmpty(response.Payload.UserName)) {
                        me.viewModel.UserName(response.Payload.UserName);
                    }
                    if (!Util.isNullOrEmpty(response.Payload.FirstName)) {
                        me.viewModel.FirstName(response.Payload.FirstName);
                    }
                    if (!Util.isNullOrEmpty(response.Payload.LastName)) {
                        me.viewModel.LastName(response.Payload.LastName);
                    }
                    me.viewModel.Email(response.Payload.Email);
                    if (me.viewModel.ID() === 0) {
                        me.viewModel.ShowPassword(true);
                    }
                    me.viewModel.IsActive(response.Payload.IsActive);
                    me.viewModel.UserCustomerIsActive(response.Payload.UserCustomerIsActive);

                    $(response.Payload.Roles).each((index: number, roles: HTMLElement) => {
                        me.viewModel.Roles.push(me.Roles(roles));
                    });

                    _.forEach(response.Payload.TimeZones, (timezone: Data.ICodeModel<number>) => {
                        me.viewModel.TimeZones.push(timezone);
                    });

                    me.viewModel.TimeZoneID(response.Payload.TimeZoneID);
                    me.viewModel.ResetPasswordOnNextLogin(response.Payload.ResetPasswordOnNextLogin);

                    me.fieldsOnLoad = ko.toJSON(me.viewModel);

                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }

            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private isValid(): boolean {
        const user: any = this.viewModel;
        let isValid: boolean = true;

        if (this.viewModel.errors().length > 0) {
            this.viewModel.errors.showAllMessages();
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            return false;
        }

        if (user.ShowPassword() && (user.Password() !== user.ConfirmPassword())) {
            UI.showErrorMessage(["Passwords do not match"]);
            user.Errors(true);
            return false;
        }

        isValid = UI.setRequiredNumber(user.TimeZoneID, user.IsTimeZoneIDMissing) ? isValid : false;

        return isValid;
    }

    private Roles(roles: any): any {
        return {
            ID: ko.observable(roles ? roles.ID : 0),
            RoleName: ko.observable(roles ? roles.RoleName : ""),
            IsUserInRole: ko.observable(roles ? roles.IsUserInRole : false),
        };
    }

    private toggleUserStatus(): void {
        const me: UserController = this;
        const newIsActive: boolean = !this.viewModel.UserCustomerIsActive();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(me.container);

        UserData.ChangeUserStatus(this.viewModel.ID(), newIsActive)
            .done((response: Data.IResponse<any>) => {
                if (response.Success) {
                    me.viewModel.UserCustomerIsActive(newIsActive);
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

    }

    private loadPasswordRequirements(): void {
        const me: UserController = this;

        UserData.GetPasswordRequirements()
            .done((response: Data.IResponse<UserData.IPasswordRequirementsResponse>) => {
                if (response.Success) {
                    me.viewModel.MinimumPasswordLength(response.Payload.MinimumPasswordLength);
                    me.viewModel.PasswordComplexityRequirements(response.Payload.PasswordComplexityRequirements);
                }
            });
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/user.ts